import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { AiBadge, StateBadge, hueForSeverity } from '@/components/forge/StatusBadge'
import { Mono, Num } from '@/components/forge/primitives'
/* `useToast` went with the three sections that used to fire one instead of
   navigating. Every row on this tab now links to the record it names. */
import { ACTION_SECTIONS, anomalyPath, anomalyPlaceName, toRcaSections } from '@/features/anomalies/api'
import { useUserNames } from '@/features/users/queries'
import { assigneeNames, assigneeText } from '@/features/workOrders/api'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/cn'
/* `ON_TINT_CLASS` left with the retired document thumbnails — they tinted a
   glyph per file type, which the live list has no field for. */

/**
 * Anomaly · Action.
 *
 * What to do about it: the corrective plan, the documents that govern it, the
 * times this happened before, and the work orders carrying it out.
 *
 * ── Every section on this tab is now a record ───────────────────────────
 * `similar_anomalies`, `document_references` and `work_orders` arrive on
 * `AnomalyOut`, resolved server-side; `recommended_actions` is a section of
 * `content`. The two that were literals — the corrective plan and the activity
 * log — are gone rather than kept beside the real ones, and the demo notice went
 * with them. There is nothing left on this tab to disclaim.
 *
 * ── The match percentage is gone and is not replaced ─────────────────────
 * `AnomalySimilarity` is a directed link and nothing more: one anomaly is
 * considered similar to another, with no score on the row. Printing a number
 * would mean inventing the one figure a reader would most reasonably trust.
 */

/*
 * ── The RETIRED literal sets ─────────────────────────────────────────────
 * `PLAN`, `PLAN_TONE` and `PLAN_PRIORITY` went with the corrective-plan card —
 * five invented steps under three invented priorities. `content.recommended_actions`
 * is what replaced them, and it carries no priority, so neither does the card.
 *
 * `DOCS`, `SIMILAR` and `WO_FIELDS` are records now — `document_references`,
 * `similar_anomalies` and `work_orders` on the anomaly's detail read. Kept as a
 * record of the design's intent, which in two cases is still worth something:
 * the document rows carried a file-type thumbnail with the extension on it,
 * which the live list cannot do because `DocumentType` says what a document IS
 * and not what format it arrived in; and the work-order card was an AI-authored
 * PROPOSAL — priority, start, duration, skills, parts — handed to a pre-filled
 * form, which is a genuinely better shape than a list of orders somebody has
 * already raised. Neither has a field behind it.
 *
 * const DOCS = [
 *   { key: 'manual', ext: 'PDF', tone: 'red', icon: 'doc' },
 *   { key: 'sop', ext: 'DOC', tone: 'watch', icon: 'list' },
 *   { key: 'pid', ext: 'DWG', tone: 'green', icon: 'layers' },
 *   { key: 'priorWo', ext: 'WO', tone: 'amber', icon: 'wrench' },
 * ]
 * const SIMILAR = [
 *   { key: 'alr3021', tone: 'red', match: 94 },
 *   { key: 'alr2740', tone: 'amber', match: 81 },
 *   { key: 'alr2109', tone: 'watch', match: 73 },
 * ]
 * const WO_FIELDS = ['priority', 'start', 'duration', 'skills', 'parts']
 */

export function ActionTab({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])

  /* All three off the detail read — no request of their own. The detail stream
     wakes on these child rows too, so a similarity the engine adds while this
     tab is open arrives without a refresh. */
  const references = anomaly?.documentReferences ?? []
  const similar = anomaly?.similarAnomalies ?? []
  const workOrders = anomaly?.workOrders ?? []
  /* The rows carry `assignee_ids` only; the directory names them. */
  const userNames = useUserNames()
  /* The fourth is off `content`, not off a column of its own — one section of
     the analysis, read on the tab it is about. `toRcaSections` filters an empty
     one out, so this is `[]` on an anomaly nobody has analysed. */
  const actions = toRcaSections(anomaly?.content, ACTION_SECTIONS)[0]?.items ?? []

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_21rem]">
      <div className="flex min-w-0 flex-col gap-4">
        {/* ── What to do about it, from the analysis itself ─────────────
            `content.recommended_actions`, the one section of the engine's report
            that is not a finding about the past. It is read here rather than on
            the Analysis tab because it is the subject of this one, and because
            somebody who has decided to act does not want to go back to the
            diagnosis to find the list.

            ── This replaces the corrective plan, which was five literals ──
            That card drew `isolate · spectrum · compare · replace · baseline`
            with a High/Medium/Low priority beside each, under an AI-drafted
            badge. Nothing in the schema proposes a step, and nothing proposes a
            priority — the badges were the invented part most likely to be
            believed, because a ranked plan is exactly what somebody would act on
            without checking.

            The engine's rows carry no priority either. They are numbered, in the
            order it wrote them, and nothing here asserts that the order is a
            ranking. The AI badge stays, because this IS machine-authored. */}
        {actions.length ? (
          <Panel>
            <div className="flex items-center gap-2">
              <Eyebrow className="flex-1">
                {t('anomalies.detail.section.recommended_actions')}
              </Eyebrow>
              <AiBadge label={t('anomalies.action.aiDrafted')} />
            </div>
            <ol className="m-0 mt-2 list-none p-0">
              {actions.map((item, index) => (
                <li
                  key={`action-${index}`}
                  className="flex items-start gap-3 border-b border-hairline py-3 last:border-0"
                >
                  <Num
                    aria-hidden="true"
                    className="flex size-6 shrink-0 items-center justify-center rounded-full border border-hairline bg-sunken text-micro font-bold"
                  >
                    {index + 1}
                  </Num>
                  <div className="min-w-0 flex-1">
                    <div className="text-body-sm font-semibold">{item.text}</div>
                    {/* "Would confirm or rule out: Cavitation." — the engine says
                        what each step is FOR, which is what makes the list a
                        diagnostic plan rather than a chore list. */}
                    {item.description ? (
                      <div className="mt-0.5 text-micro font-normal text-ink-dim">
                        {item.description}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        ) : null}

        {/* ── The documents this analysis cited ─────────────────────────
            `document_references`, pinned to a REVISION rather than to a
            document: `revision` is the numbered version that was actually read,
            which is the only honest way to cite a file that changes underneath
            you. The row states it.

            ── The row opens THAT document, not the register ─────────────
            `?doc=<id>` opens the drawer on it. It used to link to the register
            unqualified, which left somebody who had clicked a named revision to
            find it again in an unfiltered list — the one thing a citation is
            supposed to save them.

            The id and not the revision: `DocumentDrawer` reads the document and
            shows its current revision with its history, and there is no route
            that opens an OLD one. A reference pinned to revision 1 of a file now
            on revision 3 therefore opens the file, not the version that was
            cited. The row still prints the cited revision beside the name, so
            the difference is visible rather than silent. Recorded in
            BACKEND-GAPS.md.

            The presigned view URL is resolved by that screen on open and is not
            carried across on this link, which is why a plain id is enough. */}
        {references.length ? (
          <Panel>
            <Eyebrow>{t('anomalies.action.docs')}</Eyebrow>
            <ul className="m-0 mt-1 list-none p-0">
              {references.map((reference) => (
                <li
                  key={`${reference.documentId}-${reference.revision}`}
                  className="border-b border-hairline last:border-0"
                >
                  <Link
                    to={`/app/data-fusion/documents?doc=${reference.documentId}`}
                    className="flex w-full items-center gap-3 py-2.5 text-start no-underline transition-colors hover:text-ink"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-8 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xs border border-hairline bg-sunken"
                    >
                      <Icon name="doc" size="xs" className="text-ink-mute" />
                      {/* The REVISION, where the demo drew a file extension.
                          Every document in the register is a PDF — the upload
                          purpose accepts nothing else — so an extension would
                          say the same word on every row, and the revision is
                          the thing a reader actually needs. */}
                      <Mono className="text-[6.5px] font-bold tracking-wide text-ink-mute">
                        <Num>r{reference.revision}</Num>
                      </Mono>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-body-sm font-semibold">{reference.name}</span>
                      <span className="mt-0.5 block text-micro font-normal text-ink-dim">
                        {t(`dataFusion.docs.type.${reference.documentType}`, {
                          defaultValue: reference.documentType,
                        })}
                      </span>
                    </span>
                    <Icon name="right" size="sm" className="shrink-0 text-ink-mute mirror-rtl" />
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        {/* ── When this happened before ─────────────────────────────────
            `similar_anomalies`. The demo's three rows were literal ids that went
            nowhere and each carried a match percentage: 94%, 81%, 73%.

            ── These are SUMMARIES, not list items ───────────────────────
            `AnomalySummary` is `{id, serial_number, title, severity, status}` and
            nothing else — no `created_at`, no `hierarchy`, no `has_analysis`.
            `toAnomaly` maps them defensively, so the machine name and the date
            below are absent rather than wrong, and each row is conditional on
            its field for that reason. This comment used to claim they arrived as
            full list items; they never did.

            There is no percentage here and there will not be one.
            `AnomalySimilarity` is a directed link with no score on it, and a
            figure invented in this file is the one thing on the card a reader
            would take at face value. */}
        {similar.length ? (
          <Panel>
            <Eyebrow>{t('anomalies.action.similar')}</Eyebrow>
            <ul className="m-0 mt-1 list-none p-0">
              {similar.map((entry) => (
                <li key={entry.id} className="border-b border-hairline last:border-0">
                  <Link
                    to={anomalyPath(entry.id)}
                    className="flex w-full items-center gap-3 py-2.5 text-start no-underline"
                  >
                    <span
                      aria-hidden="true"
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ background: `var(--hue-${hueForSeverity(entry.severity) ?? 'forge'})` }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-body-sm font-semibold">{entry.title}</span>
                      <span className="mt-0.5 block text-micro font-normal text-ink-dim">
                        <Mono>{entry.serialNumber}</Mono>
                        {anomalyPlaceName(entry.hierarchy) ? ` · ${anomalyPlaceName(entry.hierarchy)}` : ''}
                        {entry.createdAt
                          ? ` · ${formatDateTime(entry.createdAt, { dateStyle: 'medium' })}`
                          : ''}
                      </span>
                    </span>
                    <StateBadge tone={hueForSeverity(entry.severity) ?? 'forge'} dot={false}>
                      {t(`common:severity.${entry.severity}`, { defaultValue: entry.severity })}
                    </StateBadge>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

      </div>

      <aside className="flex flex-col gap-4">
        {/* ── The work raised against this anomaly ──────────────────────
            `work_orders` on the detail read, resolved server-side and mapped by
            the work-orders module's own mapper — so `overdue` here and `overdue`
            on the board are the same computation rather than two.

            ── This is a different card from the one it replaces ────────────
            The demo's was an AI-authored PROPOSAL: priority, start, duration,
            skills and parts, handed to a pre-filled form. That is the better
            card and it has no data behind it — nothing in the schema proposes a
            schedule — so what is here instead is what exists: the orders
            somebody has actually raised, and a button to raise another.

            The button carries the anomaly across, which is the one thing the
            proposal card was really for. `verify_anomaly_matches_parent` compares
            the order's parent trio against the anomaly's exactly and 400s on a
            mismatch, so the link is what decides the placement — see
            `WorkOrderFormDialog`. Passing the id alone is enough and is correct;
            passing an asset as well would be this screen guessing at a triple
            the server is about to check. */}
        <Panel flush>
          <div className="border-b border-hairline px-4 py-3">
            <Eyebrow>{t('anomalies.action.workOrder')}</Eyebrow>
          </div>
          <div className="p-4">
            {workOrders.length ? (
              <ul className="m-0 mb-3.5 flex list-none flex-col gap-2 p-0">
                {workOrders.map((order) => (
                  <li key={order.id}>
                    <Link
                      to={`/app/work-orders?wo=${order.id}`}
                      className="flex items-start gap-2.5 rounded-sm border border-hairline px-3 py-2 no-underline transition-colors hover:border-forge"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-body-sm font-semibold text-ink">
                          {order.titleText}
                        </span>
                        <span className="mt-0.5 block text-micro font-normal text-ink-dim">
                          <Mono>{order.serialNumber}</Mono> ·{' '}
                          {t(`workOrders.state.${order.status}`, { defaultValue: order.status })}
                          {' · '}
                          {assigneeText(assigneeNames(order, userNames), t)}
                          {order.dueDate ? (
                            <Num>
                              {' · '}
                              {formatDateTime(order.dueDate, { dateStyle: 'medium' })}
                            </Num>
                          ) : null}
                        </span>
                      </span>
                      {/* Derived from `due_date` and `status` by the work-orders
                          mapper, not served — so it cannot disagree with the
                          board's own badge. */}
                      {order.overdue ? (
                        <StateBadge tone="red" dot={false}>
                          {t('workOrders.overdue')}
                        </StateBadge>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="m-0 mb-3.5 text-body-sm text-ink-dim">
                {t('anomalies.action.noWorkOrders')}
              </p>
            )}

            <ForgeButton asChild variant="primary" className="w-full">
              <Link to={`/app/work-orders?new=1&alertId=${anomaly?.id ?? ''}`}>
                {t('anomalies.action.createWo')}
              </Link>
            </ForgeButton>
            <p className="m-0 mt-2 text-center text-micro font-normal text-ink-mute">
              {t('anomalies.action.prefilled')}
            </p>
          </div>
        </Panel>

        {/* ── The activity log is gone ──────────────────────────────────
            Three literal rows — generated, acknowledged, escalated — with three
            literal timestamps, on a card that would have been the first place
            anybody looked to find out what had happened to a record. One of the
            three was not even a state this product has: `escalated` is not a
            member of `AnomalyStatus`.

            What exists is `history`, which is real and is on Analysis →
            Reasoning. Nothing is served that records a person's actions on an
            anomaly — `_append_history` carries no actor, and status changes are
            not journalled at all — so there is no honest version of this card to
            build. It is in BACKEND-GAPS.md. */}
      </aside>
    </div>
  )
}

function Eyebrow({ className, children }) {
  return (
    <div className={cn('text-micro font-semibold tracking-wide text-ink-mute uppercase', className)}>
      {children}
    </div>
  )
}
